import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { LOCAL_STORAGE_KEY } from "./constant/constant";
import { showErrorNotification } from "@/components/common/toast";

/** Default API timeout (in milliseconds) from environment variables */
const VITE_PUBLIC_API_TIMEOUT_IN_SEC = import.meta.env
  .VITE_PUBLIC_API_TIMEOUT_IN_SEC;

/**
 * Custom Base Query
 * ------------------------------------------------------------
 * Wraps RTK Query's fetchBaseQuery to add:
 * - Timeout handling
 * - Abort controller
 * - Enhanced error handling
 *
 * @param {Object} config - Base query configuration
 * @param {string} config.baseUrl - API base URL
 * @param {function} [config.prepareHeaders] - Optional header preparation function
 * @param {number} [config.timeout] - Optional custom timeout (ms)
 * @returns {function} - Async function compatible with RTK Query
 */
const customBaseQuery = ({
  baseUrl,
  prepareHeaders,
  timeout = VITE_PUBLIC_API_TIMEOUT_IN_SEC * 1000 || 20000,
}) => {
  return async (args, api, extraOptions = {}) => {
    // Initialize AbortController to handle request timeouts
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      const timeoutError =
        "The request took too long to complete. Please try again.";
      showErrorNotification(timeoutError);
    }, timeout);

    const baseQuery = fetchBaseQuery({
      baseUrl,
      prepareHeaders: prepareHeaders || ((headers) => headers),
    });

    try {
      // Execute the base query with abort signal
      const result = await baseQuery(
        { ...args, signal: controller.signal },
        api,
        extraOptions
      );

      clearTimeout(timeoutId); // Clear timeout if successful
      return result;
    } catch (error) {
      clearTimeout(timeoutId); // Clear timeout on error
      throw error; // Re-throw for higher-level handling
    }
  };
};

/**
 * Base Query Function
 * ------------------------------------------------------------
 * Higher-level wrapper for RTK Query to handle:
 * - Timeout errors
 * - Network errors
 * - Parsing errors
 * - Server errors (5xx)
 * - Unauthorized errors (401) with automatic logout
 *
 * Usage: pass config object with `baseUrl` & optional `prepareHeaders`
 *
 * @param {Object} config - Custom configuration for base query
 * @returns {function} - Async function to use in createApi's baseQuery
 */
export const baseQueryFn =
  (config) =>
  async (args, api, extraOptions = {}) => {
    const baseQuery = customBaseQuery(config);
    const showError =
      extraOptions.showError !== undefined ? extraOptions.showError : true;

    let result;
    try {
      result = await baseQuery(args, api, extraOptions.showError);
    } catch (error) {
      // ------------------- Timeout Handling -------------------
      if (error.name === "AbortError") {
        const timeoutError =
          "The request took too long to complete. Please try again.";
        showError && showErrorNotification(timeoutError);
        return { error: { status: "TIMEOUT_ERROR", message: timeoutError } };
      } else {
        // ------------------- Unexpected Errors -------------------
        console.error("Unexpected error:", error);
        const unexpectedError =
          "An unexpected error occurred. Please try again.";
        showError && showErrorNotification(unexpectedError);
        return { error: { status: "UNKNOWN_ERROR", message: unexpectedError } };
      }
    }

    // ------------------- Network / Fetch / Parsing Errors -------------------
    if (
      !result ||
      (result.error &&
        (result.error.status === "FETCH_ERROR" ||
          result.error.status === "NETWORK_ERROR"))
    ) {
      const networkError =
        "Network error occurred. Please check your connection.";
      showError && showErrorNotification(networkError);
    } else if (result?.error?.status === "PARSING_ERROR") {
      const parsingError =
        "The request took too long to complete. Please try again.";
      showErrorNotification(parsingError);
    } else if (result.error?.status === "TIMEOUT_ERROR") {
      const timeoutError =
        "The request took too long to complete. Please try again.";
      showError && showErrorNotification(timeoutError);
    } else if (result.error?.status >= 500 && result.error?.status < 600) {
      const serverError = `Server error (${result.error.status}): Please try again later.`;
      showErrorNotification(serverError);
    } else if (
      result.error?.status === 401 ||
      result?.data?.response?.statusCode === 401
    ) {
      // ------------------- Unauthorized (401) Handling -------------------
      console.error("Request failed with status code:", result.error.status);
      localStorage.removeItem(LOCAL_STORAGE_KEY.AUTH_TOKEN);
      localStorage.removeItem(LOCAL_STORAGE_KEY.USER_INFO);
      sessionStorage.removeItem(LOCAL_STORAGE_KEY.AUTH_TOKEN);
      sessionStorage.removeItem(LOCAL_STORAGE_KEY.USER_INFO);
      window.location.href = "/auth/login"; // Force redirect to login
    }

    return result;
  };
